import React, { useEffect, useState, useMemo } from "react";
import api from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Gift, TrendingUp, Users, Wallet, Settings as SettingsIcon, Search, Plus, Minus, Eye,
} from "lucide-react";
import SafeHtml from "./SafeHtml";

const fmtDT = (iso) => iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—";

export default function ReferralAdminTab() {
  return (
    <div className="space-y-5" data-testid="adm-tab-referral-root">
      <Tabs defaultValue="overview">
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="overview" data-testid="adm-ref-tab-overview"><TrendingUp className="w-4 h-4 mr-1" />Vue globale</TabsTrigger>
          <TabsTrigger value="users" data-testid="adm-ref-tab-users"><Users className="w-4 h-4 mr-1" />Utilisateurs</TabsTrigger>
          <TabsTrigger value="settings" data-testid="adm-ref-tab-settings"><SettingsIcon className="w-4 h-4 mr-1" />Règles d'utilisation</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-4"><Overview /></TabsContent>
        <TabsContent value="users" className="mt-4"><UsersBoard /></TabsContent>
        <TabsContent value="settings" className="mt-4"><SettingsBoard /></TabsContent>
      </Tabs>
    </div>
  );
}

function Overview() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/referral/overview").then((r) => setData(r.data)).catch(() => setData({ error: true })); }, []);
  if (!data) return <div className="p-8 text-center text-slate-500">Chargement…</div>;
  if (data.error) return <div className="p-8 text-center text-red-500">Erreur de chargement</div>;
  const s = data.stats;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Crédits en circulation" value={s.total_credits_in_circulation.toLocaleString("fr-FR")} testId="kpi-credits" icon={<Wallet className="w-4 h-4" />} />
        <KPI label="Utilisateurs avec crédits" value={`${s.users_with_credits} / ${s.users_total}`} testId="kpi-users" icon={<Users className="w-4 h-4" />} />
        <KPI label="Parrainages aboutis" value={s.successful_referrals} testId="kpi-successful" icon={<Gift className="w-4 h-4" />} />
        <KPI label="Mouvements totaux" value={Object.values(data.by_kind).reduce((acc, x) => acc + (x.count || 0), 0)} testId="kpi-tx" icon={<TrendingUp className="w-4 h-4" />} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-soft p-5">
          <h3 className="font-bold text-slate-900 mb-3" style={{ fontFamily: "Outfit" }}>Mouvements par type</h3>
          {Object.keys(data.by_kind).length === 0 ? <p className="text-sm text-slate-500">Aucun</p> : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-widest text-slate-500 border-b">
                <tr><th className="text-left p-2">Type</th><th className="text-right p-2">Nb</th><th className="text-right p-2">Total crédits</th></tr>
              </thead>
              <tbody>
                {Object.entries(data.by_kind).map(([k, v]) => (
                  <tr key={k} className="border-b last:border-0">
                    <td className="p-2"><Badge>{k}</Badge></td>
                    <td className="p-2 text-right">{v.count}</td>
                    <td className={`p-2 text-right font-medium ${v.total < 0 ? "text-red-600" : "text-emerald-700"}`}>{v.total > 0 ? "+" : ""}{v.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="card-soft p-5">
          <h3 className="font-bold text-slate-900 mb-3" style={{ fontFamily: "Outfit" }}>Top parrains (par crédits reçus)</h3>
          {data.top_earners.length === 0 ? <p className="text-sm text-slate-500">Aucun parrainage abouti pour le moment.</p> : (
            <ul className="space-y-2 text-sm">
              {data.top_earners.map((t, i) => (
                <li key={t.user_id} className="flex items-center justify-between gap-2 p-2 rounded hover:bg-slate-50">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex w-6 h-6 items-center justify-center rounded-full bg-amber-100 text-amber-700 font-bold text-xs">{i + 1}</span>
                    <div>
                      <div className="font-medium">{t.name || "—"}</div>
                      <div className="text-xs text-slate-400">{t.email}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-emerald-700">+{t.earned}</div>
                    <div className="text-xs text-slate-400">{t.count} parrainage(s)</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="card-soft p-5">
        <h3 className="font-bold text-slate-900 mb-3" style={{ fontFamily: "Outfit" }}>Transactions récentes (50)</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left p-2">Date</th>
                <th className="text-left p-2">Utilisateur</th>
                <th className="text-left p-2">Type</th>
                <th className="text-left p-2">Raison</th>
                <th className="text-right p-2">Crédits</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_transactions.map((t) => (
                <tr key={t.tx_id} className="border-t">
                  <td className="p-2 whitespace-nowrap">{fmtDT(t.created_at)}</td>
                  <td className="p-2">{t.user_name || t.user_id}</td>
                  <td className="p-2"><Badge>{t.kind}</Badge></td>
                  <td className="p-2 text-xs text-slate-600">{t.reason}</td>
                  <td className={`p-2 text-right font-semibold ${(t.amount || 0) < 0 ? "text-red-600" : "text-emerald-700"}`}>{t.amount > 0 ? "+" : ""}{t.amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function UsersBoard() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [minCredits, setMinCredits] = useState(0);
  const [target, setTarget] = useState(null);
  const [adjustOpen, setAdjustOpen] = useState(null);
  const reload = () => api.get("/admin/referral/users", { params: { q: q || undefined, min_credits: minCredits, limit: 200 } })
    .then((r) => setList(r.data));
  useEffect(() => { reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const search = (e) => { e?.preventDefault(); reload(); };
  return (
    <div className="space-y-4">
      <form onSubmit={search} className="card-soft p-4 flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[200px]">
          <Label>Recherche (nom, email, code parrain)</Label>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ex: lucas" data-testid="adm-ref-users-q" />
        </div>
        <div>
          <Label>Crédits min.</Label>
          <Input type="number" min="0" value={minCredits} onChange={(e) => setMinCredits(Number(e.target.value) || 0)} className="w-28" data-testid="adm-ref-users-min" />
        </div>
        <Button type="submit" className="rounded-full bg-sky-700 hover:bg-sky-600 text-white" data-testid="adm-ref-users-search">
          <Search className="w-3 h-3 mr-1" />Filtrer
        </Button>
      </form>

      <div className="card-soft overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left p-2">Nom</th>
              <th className="text-left p-2">Email</th>
              <th className="text-left p-2">Rôle</th>
              <th className="text-left p-2">Code parrain</th>
              <th className="text-right p-2">Crédits</th>
              <th className="text-right p-2">Filleuls</th>
              <th className="text-right p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan="7" className="p-8 text-center text-slate-500">Aucun résultat</td></tr>
            ) : list.map((u) => (
              <tr key={u.user_id} className="border-t hover:bg-slate-50" data-testid={`adm-ref-row-${u.user_id}`}>
                <td className="p-2 font-medium">{u.name}</td>
                <td className="p-2 text-xs text-slate-500">{u.email}</td>
                <td className="p-2"><Badge>{u.role}</Badge></td>
                <td className="p-2 font-mono text-xs">{u.referral_code || "—"}</td>
                <td className="p-2 text-right font-semibold text-sky-700">{u.referral_credits}</td>
                <td className="p-2 text-right">{u.filleuls_count}</td>
                <td className="p-2 text-right">
                  <div className="inline-flex gap-1 flex-wrap justify-end">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => setTarget(u.user_id)} data-testid={`adm-ref-view-${u.user_id}`}>
                      <Eye className="w-3 h-3 mr-1" />Détail
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => setAdjustOpen({ user_id: u.user_id, name: u.name, current: u.referral_credits })} data-testid={`adm-ref-adjust-${u.user_id}`}>
                      <Plus className="w-3 h-3 mr-1" />/<Minus className="w-3 h-3" />Crédits
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <UserDetailDialog userId={target} onClose={() => setTarget(null)} />
      <AdjustDialog adjust={adjustOpen} onClose={(updated) => { setAdjustOpen(null); if (updated) reload(); }} />
    </div>
  );
}

function UserDetailDialog({ userId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!userId) return;
    setData(null);
    api.get(`/admin/referral/users/${userId}`).then((r) => setData(r.data)).catch((e) => {
      toast.error(e.response?.data?.detail || "Erreur");
      onClose?.();
    });
  }, [userId, onClose]);
  return (
    <Dialog open={!!userId} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="adm-ref-detail-dialog">
        <DialogHeader>
          <DialogTitle>Parrainage — {data?.user?.name || "Utilisateur"}</DialogTitle>
          <DialogDescription>{data?.user?.email}</DialogDescription>
        </DialogHeader>
        {!data ? <div className="p-8 text-center text-slate-500">Chargement…</div> : (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="card-soft p-3"><div className="text-xs text-slate-500">Solde crédits</div><div className="text-2xl font-bold text-sky-700">{data.user.referral_credits}</div></div>
              <div className="card-soft p-3"><div className="text-xs text-slate-500">Code parrain</div><div className="text-base font-mono">{data.user.referral_code || "—"}</div></div>
              <div className="card-soft p-3"><div className="text-xs text-slate-500">Filleuls</div><div className="text-2xl font-bold">{data.filleuls.length}</div></div>
            </div>
            {data.parrain && (
              <div className="card-soft p-3 bg-sky-50 text-sm">
                <Gift className="inline w-4 h-4 mr-1 text-sky-700" />Parrainé par <strong>{data.parrain.name}</strong> ({data.parrain.email})
              </div>
            )}
            <div>
              <h4 className="font-semibold text-sm text-slate-700 mb-2">Filleuls ({data.filleuls.length})</h4>
              {data.filleuls.length === 0 ? <p className="text-sm text-slate-500">Aucun filleul</p> : (
                <table className="w-full text-sm card-soft">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr><th className="text-left p-2">Nom</th><th className="text-left p-2">Email</th><th className="text-left p-2">Inscrit</th><th className="text-right p-2">Statut</th></tr>
                  </thead>
                  <tbody>
                    {data.filleuls.map((f) => (
                      <tr key={f.user_id} className="border-t">
                        <td className="p-2">{f.name}</td>
                        <td className="p-2 text-xs text-slate-500">{f.email}</td>
                        <td className="p-2 text-xs">{fmtDT(f.created_at)}</td>
                        <td className="p-2 text-right">
                          {f.credited ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Crédité +{f.credited_amount}</span>
                            : <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">En attente</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div>
              <h4 className="font-semibold text-sm text-slate-700 mb-2">Transactions ({data.transactions.length})</h4>
              {data.transactions.length === 0 ? <p className="text-sm text-slate-500">Aucune</p> : (
                <table className="w-full text-sm card-soft">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr><th className="text-left p-2">Date</th><th className="text-left p-2">Type</th><th className="text-left p-2">Raison</th><th className="text-right p-2">Crédits</th></tr>
                  </thead>
                  <tbody>
                    {data.transactions.map((t) => (
                      <tr key={t.tx_id} className="border-t">
                        <td className="p-2 text-xs">{fmtDT(t.created_at)}</td>
                        <td className="p-2"><Badge>{t.kind}</Badge></td>
                        <td className="p-2 text-xs">{t.reason}</td>
                        <td className={`p-2 text-right font-medium ${(t.amount || 0) < 0 ? "text-red-600" : "text-emerald-700"}`}>{t.amount > 0 ? "+" : ""}{t.amount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AdjustDialog({ adjust, onClose }) {
  const [delta, setDelta] = useState(0);
  const [reason, setReason] = useState("Geste commercial");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDelta(0); setReason("Geste commercial"); }, [adjust]);
  const submit = async () => {
    if (!delta) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/referral/adjust", { user_id: adjust.user_id, delta, reason });
      toast.success(`Nouveau solde: ${data.new_balance} crédits`);
      onClose?.(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!adjust} onOpenChange={(v) => !v && onClose?.(false)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ajuster les crédits de {adjust?.name}</DialogTitle>
          <DialogDescription>Solde actuel : <strong>{adjust?.current} crédits</strong>. Saisissez une valeur positive (créditer) ou négative (débiter).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Delta (crédits)</Label>
            <Input type="number" value={delta} onChange={(e) => setDelta(Number(e.target.value))} placeholder="ex: 50 ou -20" data-testid="adm-ref-adjust-delta" />
          </div>
          <div>
            <Label>Raison</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="adm-ref-adjust-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => onClose?.(false)}>Annuler</Button>
          <Button className="rounded-full bg-sky-700 hover:bg-sky-600 text-white"
            onClick={submit} disabled={busy || !delta} data-testid="adm-ref-adjust-submit">
            {busy ? "…" : "Confirmer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SettingsBoard() {
  const [s, setS] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const reload = () => api.get("/admin/referral/settings").then((r) => { setS(r.data); setForm(r.data); });
  useEffect(() => { reload(); }, []);
  const dirty = useMemo(() => s && JSON.stringify(s) !== JSON.stringify(form), [s, form]);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/referral/settings", form);
      toast.success("Règles mises à jour");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  if (!s) return <div className="p-8 text-center text-slate-500">Chargement…</div>;
  return (
    <div className="space-y-4">
      <div className="card-soft p-5 bg-amber-50 border border-amber-200 text-sm">
        ⚠️ Toute modification s'applique immédiatement à <strong>tous les utilisateurs</strong>. Les soldes existants ne sont pas modifiés mais les nouveaux parrainages utilisent les valeurs à jour.
      </div>
      <div className="card-soft p-5 space-y-4 max-w-2xl">
        <SettingField label="Bonus parrainage (crédits)" hint="Crédits offerts au parrain ET au filleul à la 1ère réservation payée du filleul."
          value={form.bonus_credits} onChange={(v) => setForm({ ...form, bonus_credits: v })} min={0} max={10000} testId="set-bonus" />
        <SettingField label="Coût frais de service (crédits)" hint="Nombre de crédits pour offrir les frais de service (commission plateforme 10%) sur un cours."
          value={form.redeem_service_fee_cost} onChange={(v) => setForm({ ...form, redeem_service_fee_cost: v })} min={1} max={100000} testId="set-svc" />
        <SettingField label="Plafond transferts/jour (crédits)" hint="Limite anti-abus sur les transferts entre clients (par utilisateur, par jour)."
          value={form.max_transfer_per_day} onChange={(v) => setForm({ ...form, max_transfer_per_day: v })} min={1} max={1000000} testId="set-max-transfer" />
        <div className="bg-sky-50 border border-sky-200 rounded-xl p-3 text-xs text-sky-900">
          <strong>MNS uniquement (valeurs fixes) :</strong> 100 crédits = 1 semaine de boost visibilité · 300 crédits = 1 mois d'abonnement Premium.
        </div>
      </div>

      {/* Iter40 bis: editable rich text shown to clients on their referral tab */}
      <div className="card-soft p-5 space-y-3 max-w-3xl" data-testid="set-terms-card">
        <div>
          <Label className="text-base font-semibold">📝 Texte affiché aux clients</Label>
          <p className="text-xs text-slate-500 mt-1">
            Ce texte est affiché sur la fiche Parrainage du tableau de bord client. Vous pouvez utiliser des balises HTML
            simples : <code>&lt;h3&gt;</code>, <code>&lt;p&gt;</code>, <code>&lt;ul&gt;&lt;li&gt;</code>, <code>&lt;strong&gt;</code>,
            <code>&lt;em&gt;</code>, <code>&lt;br&gt;</code>, <code>&lt;a href&gt;</code>. Comme pour les CGV/CGU.
          </p>
        </div>
        <textarea
          className="w-full border rounded-xl p-3 text-sm font-mono min-h-[260px] focus:outline-none focus:ring-2 focus:ring-sky-200"
          value={form.terms_text || ""}
          onChange={(e) => setForm({ ...form, terms_text: e.target.value })}
          placeholder="<h3>Règles d'utilisation des crédits parrainage</h3>..."
          data-testid="set-terms-text"
        />
        <div>
          <Label className="text-sm font-medium">Aperçu en direct</Label>
          {/* Iter40 ter: SafeHtml strips any tags/attrs not in the allow-list, protecting
              admins/users from XSS even if a malicious script is pasted into the textarea. */}
          <SafeHtml
            html={form.terms_text || "<em class='text-slate-400'>(Aucun texte)</em>"}
            className="mt-1 card-soft p-4 bg-emerald-50/30 border border-emerald-200 prose prose-sm max-w-none text-slate-700"
            testId="set-terms-preview"
          />
        </div>
      </div>

      <div className="flex gap-2 pt-2 sticky bottom-0 bg-white/80 backdrop-blur-sm py-3 border-t">
        <Button variant="outline" className="rounded-full" onClick={() => setForm(s)} disabled={!dirty}>Réinitialiser</Button>
        <Button className="rounded-full bg-sky-700 hover:bg-sky-600 text-white" onClick={save} disabled={!dirty || busy} data-testid="set-save">
          {busy ? "Enregistrement…" : "Enregistrer toutes les modifications"}
        </Button>
        {dirty && <span className="text-xs text-amber-700 self-center">⚠ Modifications non enregistrées</span>}
      </div>
    </div>
  );
}

function SettingField({ label, hint, value, onChange, min, max, step, float, testId }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input type="number" min={min} max={max} step={step || (float ? 0.01 : 1)}
        value={value ?? ""} onChange={(e) => onChange(float ? parseFloat(e.target.value) : parseInt(e.target.value, 10))}
        data-testid={testId} className="max-w-[200px]" />
      <p className="text-xs text-slate-500 mt-1">{hint}</p>
    </div>
  );
}

const KPI = ({ label, value, testId, icon }) => (
  <div className="card-soft p-4" data-testid={testId}>
    <div className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-1">{icon}{label}</div>
    <div className="text-2xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>{value}</div>
  </div>
);

const Badge = ({ children }) => (
  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{children}</span>
);
